import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart"
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts"
import { Icon, Title } from "@tremor/react"
import { Link } from "wouter-preact"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { currencyFormatter } from "@/utils"
import { Button } from "@/components/ui/button"
import { Download, Calculator, X } from "lucide-react"

export interface RawData {
  month: string
  [key: string]: string | number
}

export interface ChartData {
  month: string
  [key: string]: string | number
}

interface MonthlyBreakdownChartBaseProps {
  title: string
  titleTooltip: string
  chartData: ChartData[]
  chartConfig: any
  dataKeys: string[]
  showYearsInXAxis?: boolean
  downloadFilename?: string
  xAxisInterval?: number
  fullWidth?: boolean
  customTickFormatter?: (value: any) => string
  // Redistribution props
  showRedistributeButton?: boolean
  isRedistributed?: boolean
  onRedistribute?: () => void
  onResetRedistribution?: () => void
}

export interface TitleAreaProps {
  title: string
  link?: string
  titleTooltip?: string
  onDownload?: () => void
  // Redistribution props
  showRedistributeButton?: boolean
  isRedistributed?: boolean
  onRedistribute?: () => void
  onResetRedistribution?: () => void
}

export function TitleArea({ 
  title, 
  link, 
  titleTooltip, 
  onDownload,
  showRedistributeButton,
  isRedistributed,
  onRedistribute,
  onResetRedistribution
}: TitleAreaProps) {
  return (
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Title>{link ? <Link to={link}>{title}</Link> : title}</Title>
      </div>
      <div className="flex gap-2">
        {showRedistributeButton && (
          isRedistributed ? (
            <Button
              variant="outline"
              onClick={onResetRedistribution}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={onRedistribute}
            >
              <Calculator className="h-4 w-4 mr-2" />
              Redistribute Uncoded & Misc
            </Button>
          )
        )}
        {onDownload && (
          <Button
            variant="outline"
            onClick={onDownload}
            className="h-8 w-8 p-0"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

export function MonthlyBreakdownChartBase({
  title,
  titleTooltip,
  chartData,
  chartConfig,
  dataKeys,
  showYearsInXAxis,
  downloadFilename,
  xAxisInterval,
  fullWidth,
  customTickFormatter,
  showRedistributeButton,
  isRedistributed,
  onRedistribute,
  onResetRedistribution,
}: MonthlyBreakdownChartBaseProps) {
  // Check if we need to show years by looking for duplicate month names
  const monthNames = chartData.map(d => d.month.slice(0, 3))
  const hasDuplicateMonths = monthNames.length !== new Set(monthNames).size
  const shouldShowYears = showYearsInXAxis || hasDuplicateMonths

  const handleDownload = () => {
    if (!chartData.length) return

    // Create CSV content
    const headers = ['Month', ...dataKeys]
    const csvContent = [
      headers.join(','),
      ...chartData.map(row => {
        const values = [row.month, ...dataKeys.map(key => row[key] || 0)]
        return values.join(',')
      })
    ].join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = downloadFilename || `${title.toLowerCase().replace(/\s+/g, '_')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className="flex mt-4">
      <Card className={fullWidth ? "w-full" : "w-full max-w-4xl"}>
        <CardHeader>
          <TitleArea 
            title={title} 
            titleTooltip={titleTooltip} 
            onDownload={handleDownload}
            showRedistributeButton={showRedistributeButton}
            isRedistributed={isRedistributed}
            onRedistribute={onRedistribute}
            onResetRedistribution={onResetRedistribution}
          />
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig}>
            <BarChart width={600} height={300} data={chartData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="month"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                {...(xAxisInterval !== undefined && { interval: xAxisInterval })}
                tickFormatter={customTickFormatter || ((value: any) => {
                  if (shouldShowYears) {
                    // Show compact format like "Jan '24"
                    const parts = value.split(' ')
                    if (parts.length === 2) {
                      const month = parts[0]
                      const year = parts[1]
                      return `${month} '${year.slice(2)}`
                    }
                    return value
                  }
                  // Show just month like "Jan"
                  return value.slice(0, 3)
                })}
              />
              <YAxis tickFormatter={(value: any) => currencyFormatter(value)} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    // Sort payload by value (biggest to smallest)
                    const sortedPayload = [...payload].sort((a, b) => {
                      const aValue = typeof a.value === 'number' ? a.value : 0
                      const bValue = typeof b.value === 'number' ? b.value : 0
                      return bValue - aValue
                    })

                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="grid gap-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{payload[0].payload.month}</span>
                          </div>
                          {sortedPayload.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="h-2 w-2 rounded-full" 
                                  style={{ backgroundColor: entry.color || (entry as any).fill }} 
                                />
                                <span className="text-muted-foreground">{entry.name}</span>
                              </div>
                              <span className="font-mono font-medium">
                                {typeof entry.value === 'number' ? currencyFormatter(entry.value) : entry.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={chartConfig[key].color}
                  radius={[4, 4, 0, 0]}
                  name={chartConfig[key].label}
                />
              ))}
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}
